var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { StreamSession } from "../stream/";
import { call, cancelled, cancel, flush, fork, put, select, delay, takeEvery, take, join, } from "@redux-saga/core/effects";
import { buffers, channel } from "redux-saga";
import log from "../../log";
import { CallEventType, States } from "./model";
import { callAnswer, callCreateRequest, callHold, callConnectionStateChange, callStateAction, callTerminate, clearAllCalls, } from "./actions";
import { CallImpl } from "./index";
import { closeMediaStream, shouldBeRetried, retryIf, TRANSCEIVER_STOPPED_FIELD, } from "../../Common";
import { controllerPattern, onlyCallPattern } from "./patterns";
import { mqttEvent } from "../actions";
import { iceServers, browser as getBrowser, stats } from "../selectors";
import { postStatsNewSession } from "../conference/sagas";
/**
 * If registered, will fork task managers for one call on call creation events
 * @param parent - context in which task manager controller was called
 */
export function* taskManagerController(parent) {
    log.debug("taskManagerController started listening for new call events");
    yield takeEvery([controllerPattern(parent)], function* (action) {
        try {
            // translate action to local event
            if (action.type === mqttEvent.toString()) {
                action = Object.assign(Object.assign({}, action), { type: action.payload.type });
            }
            if (action.type === CallEventType.CALL_CREATED) {
                // ignore self, just in case is receives one
                if (action.payload.userEndpointId === parent.endpoint()) {
                    return;
                }
                // forward altered action to callCreateRequest
                yield put(callCreateRequest({
                    targetEndpoint: {
                        username: action.payload.userId,
                        endpoint: action.payload.userEndpointId,
                    },
                    dir: "incoming",
                    callId: action.payload.callId,
                    sdpOffer: action.payload.sdpOffer,
                }));
            }
            else if (action.type === callCreateRequest.toString()) {
                // no point in creating calls if not registered
                if (!parent.registered()) {
                    log.error("taskManagerController received remote call created event without being registered", action);
                    return;
                }
                log.debug("taskManagerController about to initiate new call");
                const target = action.payload.targetEndpoint.username;
                const context = parent.getCallContext(target);
                // make sure no other username tied call is running
                if (context.task && context.task.isRunning()) {
                    log.warn("taskManagerController asked to create new call, although one is already running, will wait before kill", action);
                    yield cancel(context.task);
                }
                const callImpl = new CallImpl(parent, action.payload.dir, action.payload.targetEndpoint, action.payload.callId);
                // notify client and stats of new call
                parent.sendEvent("newSession", callImpl);
                if (action.payload.dir === "outgoing") {
                    parent
                        .runSaga(postStatsNewSession, parent.getStatsResource(), {
                        firstInRoom: true,
                        sessionId: callImpl.id,
                        type: "call",
                    })
                        .toPromise();
                }
                context.task = yield fork(callTaskManager, callImpl, action.payload.sdpOffer);
            }
        }
        catch (error) {
            log.warn("taskManagerController error", error);
        }
    });
}
function safeSendEvent(context, event, payload) {
    if (payload) {
        context.session.sendEvent(event, payload);
    }
}
/**
 * Listens to all events concerning the given call and forwards them to state machine via channel
 * if required by state flow
 * @param session - the callImpl class
 * @param options - ICallOptions
 * @param sdpOffer - if incoming this will be defined
 */
function* callTaskManager(session, sdpOffer) {
    const context = {
        session,
        channel: yield call(channel, buffers.expanding()),
        icechannel: yield call(channel, buffers.expanding()),
        rtc: null,
        localStream: null,
        remoteStream: null,
        sdpOffer,
        canSendIceCandidates: false,
    };
    const callT = yield fork(callTask, context);
    yield fork(sendCallIceCandidatesSaga, context);
    log.debug("callTaskManager started listening call-type events");
    yield takeEvery([onlyCallPattern(session.id)], function* (action) {
        // translate action to local event
        if (action.type === mqttEvent.toString()) {
            action = Object.assign(Object.assign({}, action), { type: action.payload.type });
        }
        switch (action.type) {
            case CallEventType.CALL_REJECTED:
            case CallEventType.CALL_CANCELLED:
            case CallEventType.CALL_ANSWERED:
            case CallEventType.CALL_RINGING:
            case CallEventType.CALL_ENDED:
            case CallEventType.CALL_UPDATE:
            case callHold.toString():
            case callConnectionStateChange.toString():
            case callAnswer.toString():
            case clearAllCalls.toString():
            case callTerminate.toString(): {
                yield put(context.channel, action);
                break;
            }
            case CallEventType.ICE_CANDIDATES_FOUND: {
                if (!Array.isArray(action.payload.iceCandidates)) {
                    log.warn("callTaskManager got remote ice event with zero candidates");
                    break;
                }
                action.payload.iceCandidates.forEach((ic) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        // make sure remote description is set, wait for it
                        // await context.rtc.haveRemote.promise;
                        yield context.rtc.pc.addIceCandidate(ic);
                    }
                    catch (error) {
                        log.error("callTaskManager addIceCandidate remote failed with ", error);
                    }
                }));
                break;
            }
            default: {
                log.warn("callTaskManager received action that should'nt. Ignored.", action);
            }
        }
    });
    yield join(callT);
    yield cancel();
}
/**
 * State pattern for the life cycle of a call
 * @param context - see ICallContext
 */
function* callTask(context) {
    // initial state
    let state = callStateAction({ next: States.CALL });
    log.debug("callTask state has been initialized");
    try {
        while (state && state.payload) {
            log.debug("Call executing state ", state.payload);
            context.session.pointer = state.payload.next;
            switch (state.payload.next) {
                case States.CALL: {
                    state = yield* callState(context, state.payload.cause);
                    break;
                }
                case States.RINGING: {
                    state = yield* ringingState(context, state.payload.cause);
                    break;
                }
                case States.ANSWER: {
                    state = yield* answerState(context, state.payload.cause);
                    break;
                }
                case States.CANCEL: {
                    state = yield* cancelState(context, state.payload.cause);
                    break;
                }
                case States.REJECT: {
                    state = yield* rejectState(context, state.payload.cause);
                    break;
                }
                case States.ESTABLISHED: {
                    state = yield* establishedState(context, state.payload.cause);
                    break;
                }
                case States.HANGUP: {
                    state = yield* hangupState(context, state.payload.cause);
                    break;
                }
                case States.FAIL: {
                    state = yield* failState(context, state.payload.error);
                    break;
                }
                case States.CLOSE: {
                    state = yield* closeState(context, state.payload.cause);
                    break;
                }
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("Unexpected error in callTask ", error);
        }
    }
    finally {
        if (yield cancelled()) {
            yield call(closeState, context);
        }
    }
}
/**
 * if calling make create peer connection, else send ringing events and go to Ringing State
 * @param context
 * @param action
 */
function* callState(context, action) {
    try {
        if (context.session.direction === "incoming") {
            context.canSendIceCandidates = true;
            yield* retryIf(3, 1700, shouldBeRetried, [
                context.session.parent.getCallResource(),
                context.session.parent.getCallResource().create,
            ], {
                callId: context.session.id,
                userEndpointId: context.session.parent.endpoint(),
                userId: context.session.parent.identity(),
            }, { urlPostfix: "ringing" });
            context.session.sendEvent("ringing");
            return callStateAction({ next: States.RINGING, cause: action });
        }
        context.session.sendEvent("connecting");
        context.rtc = new StreamSession({ statsList: yield select(stats) });
        const sendVideo = context.session.options.mediaConstraints
            ? !(context.session.options.mediaConstraints.video === false)
            : true;
        const sendAudio = context.session.options.mediaConstraints
            ? !(context.session.options.mediaConstraints.audio === false)
            : true;
        const receiveVideo = context.session.options.receiveMediaConstraints
            ? !(context.session.options.receiveMediaConstraints.video === false)
            : true;
        const receiveAudio = context.session.options.receiveMediaConstraints
            ? !(context.session.options.receiveMediaConstraints.audio === false)
            : true;
        const skipLocalMedia = sendAudio === false && sendVideo === false;
        if (!skipLocalMedia) {
            yield call([context.rtc, context.rtc.resolveMediaStream], context.session.options.mediaConstraints);
        }
        context.localStream = {
            id: context.session.id,
            mediaStream: skipLocalMedia ? null : context.rtc.localMediaStream,
            type: "video",
            originator: {
                username: context.session.parent.identity(),
                endpoint: context.session.parent.endpoint(),
            },
            isMuted: () => false,
            getMetadata: () => {
                throw new Error("todo");
            },
        };
        context.session.sendEvent("streamAdded", context.localStream);
        yield* initRtc(context);
        if (!skipLocalMedia) {
            context.rtc.fillMedia(context.rtc.localMediaStream);
        }
        context.rtc.setDirection(["video"], sendVideo, receiveVideo);
        context.rtc.setDirection(["audio"], sendAudio, receiveAudio);
        yield call([context.rtc, context.rtc.offer]);
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            sdpOffer: context.rtc.pc.localDescription.sdp,
            target: context.session.remoteEndpoint.username,
            userEndpointId: context.localStream.originator.endpoint,
            userId: context.localStream.originator.username,
        }, { urlPostfix: "call" });
        while (true) {
            const event = yield take(context.channel);
            if (event.type === CallEventType.CALL_RINGING) {
                context.session.sendEvent("ringing");
                return callStateAction({ next: States.RINGING, cause: event });
            }
            else if (event.type === CallEventType.CALL_ANSWERED) {
                if (context.session.direction === "incoming") {
                    return callStateAction({
                        next: States.CLOSE,
                        cause: event,
                    });
                }
                context.canSendIceCandidates = true;
                return callStateAction({ next: States.ANSWER, cause: event });
            }
            else if (event.type === CallEventType.CALL_REJECTED) {
                // premature rejection e.g. in case of calling itself
                return callStateAction({ next: States.CLOSE, cause: event });
            }
            else if (event.type === clearAllCalls.toString()) {
                return callStateAction({ next: States.CANCEL, cause: event });
            }
            else if (event.type === callStateAction.toString()) {
                // forward state change
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("callState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
function* initRtc(context) {
    const store = context.session.parent.getStore();
    yield call([context.rtc, context.rtc.create], {
        iceServers: yield select(iceServers),
    });
    context.rtc.on("icecandidate", (event) => context.icechannel.put({
        candidate: event.candidate,
    }));
    context.rtc.on("connectionstatechange", () => store.dispatch(callConnectionStateChange({
        callId: context.session.id,
        state: context.rtc.pc.connectionState,
    })));
    context.rtc.on("track", (event) => (context.remoteStream = {
        id: context.session.id,
        mediaStream: event.streams[0],
        type: "video",
        originator: context.session.remoteEndpoint,
        isMuted: () => false,
        getMetadata: () => {
            throw new Error("TODO");
        },
    }));
    if (context.session.options.statistics &&
        context.session.options.statistics.enable) {
        let counter = 0;
        context.rtc.on("stats", () => __awaiter(this, void 0, void 0, function* () {
            try {
                if (++counter % 9 === 0) {
                    // once is enough for browser info
                    const data = counter === 9
                        ? Object.assign({ browser: getBrowser(context.session.parent.getStore().getState()) }, (yield context.rtc.getAllLogs())) : yield context.rtc.getLastLogs();
                    yield context.session.parent.getStatsResource().update(Object.assign({ sessionId: context.session.id, userId: context.localStream.originator.username, userEndpointId: context.localStream.originator.endpoint, role: context.session.direction, type: "call" }, data), { urlPostfix: `streams/${context.rtc.id}` });
                }
            }
            catch (error) {
                log.debug("on upload stats", error);
            }
        }));
        context.rtc.enableStats(context.session.options.statistics.interval);
    }
    log.debug(context);
}
/**
 * Phone is ringing, waiting for action by either side
 * @param context
 * @param action
 */
function* ringingState(context, action) {
    try {
        while (true) {
            const event = yield take(context.channel);
            if (event.type === CallEventType.CALL_REJECTED ||
                event.type === CallEventType.CALL_CANCELLED) {
                return callStateAction({ next: States.CLOSE, cause: event });
            }
            else if (event.type === CallEventType.CALL_ANSWERED) {
                if (context.session.direction === "incoming") {
                    return callStateAction({
                        next: States.CLOSE,
                        cause: event,
                    });
                }
                context.canSendIceCandidates = true;
                return callStateAction({ next: States.ANSWER, cause: event });
            }
            else if (event.type === callAnswer.toString()) {
                return callStateAction({ next: States.ANSWER, cause: event });
            }
            else if (event.type === callTerminate.toString() ||
                event.type === clearAllCalls.toString()) {
                if (context.session.direction === "outgoing") {
                    return callStateAction({
                        next: States.CANCEL,
                        cause: event,
                    });
                }
                else {
                    return callStateAction({
                        next: States.REJECT,
                        cause: event,
                    });
                }
            }
            else if (event.type === callStateAction.toString()) {
                // forward state change
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("ringingState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
/**
 * Answer or get answered event
 * @param context
 * @param action
 */
function* answerState(context, action) {
    try {
        if (context.session.direction === "outgoing") {
            // now its time to get remote endpoint, device from which call was answered
            context.session.remoteEndpoint.endpoint = action.payload.userEndpointId;
            yield call([context.rtc, context.rtc.setSdp], {
                type: "answer",
                sdp: action.payload.sdpAnswer,
            });
            context.session.sendEvent("accepted");
        }
        else {
            context.rtc = new StreamSession({ statsList: yield select(stats) });
            const sendVideo = context.session.options.mediaConstraints
                ? !(context.session.options.mediaConstraints.video === false)
                : true;
            const sendAudio = context.session.options.mediaConstraints
                ? !(context.session.options.mediaConstraints.audio === false)
                : true;
            const receiveVideo = context.session.options.receiveMediaConstraints
                ? !(context.session.options.receiveMediaConstraints.video === false)
                : true;
            const receiveAudio = context.session.options.receiveMediaConstraints
                ? !(context.session.options.receiveMediaConstraints.audio === false)
                : true;
            const skipLocalMedia = sendAudio === false && sendVideo === false;
            if (!skipLocalMedia) {
                yield call([context.rtc, context.rtc.resolveMediaStream], context.session.options.mediaConstraints);
            }
            context.localStream = {
                id: context.session.id,
                mediaStream: skipLocalMedia ? null : context.rtc.localMediaStream,
                type: "video",
                originator: {
                    username: context.session.parent.identity(),
                    endpoint: context.session.parent.endpoint(),
                },
                isMuted: () => false,
                getMetadata: () => {
                    throw new Error("TODO");
                },
            };
            context.session.sendEvent("streamAdded", context.localStream);
            yield* initRtc(context);
            yield call([context.rtc, context.rtc.setSdp], {
                type: "offer",
                sdp: context.sdpOffer,
            });
            if (!skipLocalMedia) {
                context.rtc.fillMedia(context.rtc.localMediaStream);
            }
            context.rtc.setDirection(["video"], sendVideo, receiveVideo);
            context.rtc.setDirection(["audio"], sendAudio, receiveAudio);
            yield call([context.rtc, context.rtc.answer]);
            yield* retryIf(3, 1700, shouldBeRetried, [
                context.session.parent.getCallResource(),
                context.session.parent.getCallResource().create,
            ], {
                callId: context.session.id,
                sdpAnswer: context.rtc.pc.localDescription.sdp,
                userEndpointId: context.localStream.originator.endpoint,
                userId: context.localStream.originator.username,
            }, { urlPostfix: "answer" });
        }
        while (true) {
            const event = yield take(context.channel);
            if (event.type === callConnectionStateChange.toString()) {
                if (event.payload.state === "connected") {
                    return callStateAction({
                        next: States.ESTABLISHED,
                        cause: event,
                    });
                }
                else if (event.payload.state === "failed") {
                    return callStateAction({
                        next: States.FAIL,
                        cause: event,
                        error: "Peer connection state changed to failed",
                    });
                }
            }
            else if (event.type === CallEventType.CALL_ENDED) {
                return callStateAction({ next: States.CLOSE, cause: event });
            }
            else if (event.type === callTerminate.toString() ||
                event.type === clearAllCalls.toString()) {
                return callStateAction({ next: States.HANGUP, cause: event });
            }
            else if (event.type === callStateAction.toString()) {
                // forward state change
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("answerState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
/**
 * Call is established, waiting for hold, hangup or fail
 * @param context
 * @param action
 */
function* establishedState(context, action) {
    try {
        safeSendEvent(context, "streamAdded", context.remoteStream);
        while (true) {
            const event = yield take(context.channel);
            if (event.type === callConnectionStateChange.toString()) {
                if (event.payload.state === "disconnected") {
                    safeSendEvent(context, "streamRemoved", context.remoteStream);
                }
                else if (event.payload.state === "connected") {
                    safeSendEvent(context, "streamAdded", context.remoteStream);
                }
                else if (event.payload.state === "failed") {
                    return callStateAction({
                        next: States.FAIL,
                        error: new Error("Peer connection state changed to failed"),
                    });
                }
            }
            else if (event.type === CallEventType.CALL_ENDED) {
                return callStateAction({ next: States.CLOSE, cause: event });
            }
            else if (event.type === callTerminate.toString() ||
                event.type === clearAllCalls.toString()) {
                return callStateAction({ next: States.HANGUP, cause: event });
            }
            else if (event.type === callHold.toString()) {
                if (event.payload.hold &&
                    !(context.session.holdStatus.local ||
                        context.session.holdStatus.remote)) {
                    yield fork(holderFlow, context, event);
                }
                else if (!event.payload.hold && context.session.holdStatus.local) {
                    yield fork(unholderFlow, context, event);
                }
            }
            else if (event.type === CallEventType.CALL_UPDATE &&
                event.payload.intention === "HOLD") {
                if (context.session.holdStatus.remote) {
                    yield fork(unholdeeFlow, context, event);
                }
                else {
                    yield fork(holdeeFlow, context, event);
                }
            }
            else if (event.type === callStateAction.toString()) {
                // forward state change
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("establishedState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
/**
 * Renegotiate a hold request, responding to such request or creating one
 * @param context
 * @param action
 */
function* holderFlow(context, action) {
    try {
        // 'does it make sense to hold' guard
        context.transDirs = [];
        for (const trans of context.rtc.pc.getTransceivers()) {
            if (!trans[TRANSCEIVER_STOPPED_FIELD] && trans.direction !== "inactive") {
                context.transDirs.push({ trans, dir: trans.direction });
                trans.direction = "inactive";
            }
        }
        if (!context.transDirs.length) {
            return;
        }
        // wait for that precious negotiationneeded flag change
        yield delay(5);
        yield call([context.rtc, context.rtc.offer]);
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            intention: "HOLD",
            sdpOfferOrAnswer: context.rtc.pc.localDescription.sdp,
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "update" });
        const event = yield take((ev) => ev.type === CallEventType.CALL_UPDATE &&
            ev.payload &&
            ev.payload.callId === context.session.id);
        yield call([context.rtc, context.rtc.setSdp], {
            type: "answer",
            sdp: event.payload.sdpOfferOrAnswer,
        });
        context.session.holdStatus.local = true;
        context.session.holdPromise.resolve();
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("holderFlow occured ", error);
            context.session.holdPromise.reject();
        }
    }
}
/**
 * Renegotiate a hold request, responding to such request or creating one
 * @param context
 * @param action
 */
function* unholderFlow(context, action) {
    try {
        for (const { trans, dir } of context.transDirs) {
            if (!trans[TRANSCEIVER_STOPPED_FIELD] && trans.direction === "inactive") {
                trans.direction = dir;
            }
        }
        // wait for that precious negotiationneeded flag change
        yield delay(5);
        yield call([context.rtc, context.rtc.offer]);
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            intention: "HOLD",
            sdpOfferOrAnswer: context.rtc.pc.localDescription.sdp,
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "update" });
        const event = yield take((ev) => ev.type === CallEventType.CALL_UPDATE &&
            ev.payload &&
            ev.payload.callId === context.session.id);
        yield call([context.rtc, context.rtc.setSdp], {
            type: "answer",
            sdp: event.payload.sdpOfferOrAnswer,
        });
        context.session.holdStatus.local = false;
        context.session.holdPromise.resolve();
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("unholderFlow occured ", error);
            context.session.holdPromise.reject();
        }
    }
}
/**
 * Renegotiate a hold request, responding to such request or creating one
 * @param context
 * @param action
 */
function* holdeeFlow(context, action) {
    try {
        // 'does it make sense to hold' guard
        context.transDirs = [];
        for (const trans of context.rtc.pc.getTransceivers()) {
            if (!trans[TRANSCEIVER_STOPPED_FIELD] && trans.direction !== "inactive") {
                context.transDirs.push({ trans, dir: trans.direction });
                trans.direction = "inactive";
            }
        }
        if (!context.transDirs.length) {
            return;
        }
        // wait for that precious negotiationneeded flag change
        yield delay(5);
        yield call([context.rtc, context.rtc.setSdp], {
            type: "offer",
            sdp: action.payload.sdpOfferOrAnswer,
        });
        yield call([context.rtc, context.rtc.answer]);
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            sdpOfferOrAnswer: context.rtc.pc.localDescription.sdp,
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "update" });
        context.session.holdStatus.remote = true;
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("holdeeFlow occured ", error);
        }
    }
}
/**
 * Renegotiate a hold request, responding to such request or creating one
 * @param context
 * @param action
 */
function* unholdeeFlow(context, action) {
    try {
        for (const { trans, dir } of context.transDirs) {
            if (!trans[TRANSCEIVER_STOPPED_FIELD] && trans.direction === "inactive") {
                trans.direction = dir;
            }
        }
        // wait for that precious negotiationneeded flag change
        yield delay(5);
        yield call([context.rtc, context.rtc.setSdp], {
            type: "offer",
            sdp: action.payload.sdpOfferOrAnswer,
        });
        yield call([context.rtc, context.rtc.answer]);
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            sdpOfferOrAnswer: context.rtc.pc.localDescription.sdp,
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "update" });
        context.session.holdStatus.remote = false;
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("unholdeeFlow occured ", error);
        }
    }
}
/**
 * Hangup call by either side
 * @param context
 * @param action
 */
function* hangupState(context, action) {
    try {
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            reason: action.payload.reason || "unspecified",
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "hangup" });
        // forward previous event to next state
        return callStateAction({ next: States.CLOSE, cause: action });
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("hangupState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
/**
 * Call was rejected by caller
 * @param context
 * @param action
 */
function* cancelState(context, action) {
    try {
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            reason: action.payload.reason || "unspecified",
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "cancel" });
        // forward previous event to next state
        return callStateAction({ next: States.CLOSE, cause: action });
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("cancelState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
/**
 * Call was rejected by callee
 * @param context
 * @param action
 */
function* rejectState(context, action) {
    try {
        yield* retryIf(3, 1700, shouldBeRetried, [
            context.session.parent.getCallResource(),
            context.session.parent.getCallResource().create,
        ], {
            callId: context.session.id,
            reason: action.payload.reason || "unspecified",
            userEndpointId: context.session.parent.endpoint(),
            userId: context.session.parent.identity(),
        }, { urlPostfix: "reject" });
        // forward previous event to next state
        return callStateAction({ next: States.CLOSE, cause: action });
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("rejectState occured ", error);
            return callStateAction({ next: States.FAIL, error });
        }
        else {
            return callStateAction({ next: States.CLOSE });
        }
    }
}
/**
 * Inform client of fail failure in any of the states, then close
 */
function* failState(context, error) {
    context.session.sendEvent("failed", error || new Error("Error not specified"));
    return callStateAction({ next: States.CLOSE });
}
function uploadFinalStats(context) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (context.session.options.statistics &&
                context.session.options.statistics.enable) {
                const logs = yield context.rtc.getAllLogs();
                context.session.parent.getStatsResource().update(Object.assign({ sessionId: context.session.id, userId: context.localStream.originator.username, userEndpointId: context.localStream.originator.endpoint, role: context.session.direction, type: "call", browser: getBrowser(context.session.parent.getStore().getState()) }, logs), { urlPostfix: `streams/${context.rtc.id}` });
            }
        }
        catch (error) {
            log.warn("on upload final stats", error);
        }
    });
}
/**
 * Exit point of the state pattern, cleanup and inform client of end of call
 * @param context
 * @param action
 */
function* closeState(context, action) {
    try {
        if (context.rtc) {
            context.rtc.close();
            uploadFinalStats(context);
        }
        if (!!context.localStream && !!context.localStream.mediaStream) {
            context.session.sendEvent("streamRemoved", context.localStream);
            if (!context.session.options.mediaStream) {
                closeMediaStream(context.rtc.localMediaStream);
            }
        }
        if (!!context.remoteStream && !!context.remoteStream.mediaStream) {
            safeSendEvent(context, "streamRemoved", context.remoteStream);
            closeMediaStream(context.rtc.remoteMediaStream);
        }
        let reason = "Call was abruptly cancelled, probably by error";
        if (action) {
            if (action.type === callTerminate.toString()) {
                reason = "Caller terminated session";
            }
            else if (action.type === clearAllCalls.toString()) {
                reason = "Application was closed abruptly";
            }
            else if (action.type === CallEventType.CALL_ENDED) {
                reason = "Remote user hanged up";
            }
            else if (action.type === CallEventType.CALL_REJECTED) {
                reason = "Callee rejected call";
            }
            else if (action.type === CallEventType.CALL_CANCELLED) {
                reason = "Caller cancelled call";
            }
            else if (action.type === CallEventType.CALL_ANSWERED) {
                reason = "Call was answered on other device";
            }
        }
        context.session.sendEvent("ended", reason);
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("closeState occured ", error);
        }
    }
    return callStateAction(null);
}
/**
 * Either side gathers ice candidates and sends them to server in bulk
 * @param context
 * @param action
 */
function* sendCallIceCandidatesSaga(context) {
    let startTime = null;
    let candidates = null;
    let numberOfCandidates = 0;
    while (true) {
        // trigger candidates epoch
        if (candidates === null) {
            candidates = [yield take(context.icechannel)];
            startTime = Date.now();
        }
        yield delay(250);
        candidates = candidates.concat(yield flush(context.icechannel));
        if (candidates.length && context.canSendIceCandidates) {
            const iceCandidates = candidates
                .filter(({ candidate }) => !!candidate)
                .map(({ candidate }) => candidate.toJSON());
            if (iceCandidates.length) {
                numberOfCandidates += iceCandidates.length;
                yield fork(retryIf, 2, 2000, shouldBeRetried, [
                    context.session.parent.getCallResource(),
                    context.session.parent.getCallResource().create,
                ], {
                    iceCandidates,
                    callId: context.session.id,
                    userId: context.localStream.originator.username,
                    userEndpointId: context.localStream.originator.endpoint,
                }, { urlPostfix: "addIceCandidates" });
            }
            if (candidates.length !== iceCandidates.length) {
                log.debug(`call ice candidate sending stops now after ${Date.now() - startTime}` + ` millis and ${numberOfCandidates} candidates found`);
                candidates = null;
                numberOfCandidates = 0;
            }
            else {
                candidates = [];
            }
        }
    }
}
